import { useEffect, useState } from "react";
import { CheckCircle2, Mic, Bot, User as UserIcon } from "lucide-react";

/**
 * Auto-looping 3-panel preview shown inside the landing-page card.
 * No video — pure Tailwind + CSS + React. Each panel is a shorthand
 * "screen-recording" of one assessment section so a visitor understands
 * what actually happens without needing to log in.
 */
const PANELS = [
  { key: "mcq",   label: "Section 1 · Timed MCQ" },
  { key: "pitch", label: "Section 2 · Sales Pitch" },
  { key: "rp",    label: "Section 3 · AI Role Play" },
];

const PANEL_MS = 5200;

export default function DemoPreview() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PANELS.length), PANEL_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="border border-[#EE2737]/30 bg-[#12121a] overflow-hidden"
      data-testid="landing-video-card"
    >
      <div className="aspect-video relative bg-[#5F004B]">
        {/* Chrome header — subtle window bar */}
        <div className="absolute top-0 inset-x-0 flex items-center gap-1.5 px-4 py-2 border-b border-white/5 z-20 bg-[#5F004B]/80 backdrop-blur">
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <div className="ml-3 text-[9px] uppercase tracking-[0.22em] text-[#EE2737] font-bold">
            {PANELS[idx].label}
          </div>
        </div>

        {/* Panels stack */}
        <div className="absolute inset-0 pt-9">
          {PANELS.map((p, i) => (
            <div
              key={p.key}
              className={`absolute inset-0 px-6 py-5 transition-all duration-700 ease-out ${
                i === idx
                  ? "opacity-100 translate-x-0"
                  : i < idx
                  ? "opacity-0 -translate-x-4 pointer-events-none"
                  : "opacity-0 translate-x-4 pointer-events-none"
              }`}
            >
              {p.key === "mcq"   && <McqPanel active={i === idx} />}
              {p.key === "pitch" && <PitchPanel active={i === idx} />}
              {p.key === "rp"    && <RolePlayPanel active={i === idx} />}
            </div>
          ))}
        </div>

        {/* Progress dots + label */}
        <div className="absolute bottom-3 inset-x-0 flex items-center justify-center gap-2 z-20">
          {PANELS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === idx ? "w-8 bg-[#EE2737]" : "w-2 bg-white/20"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Caption row under the animation */}
      <div className="px-5 py-4 border-t border-white/5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#EE2737] font-bold mb-0.5">
            3-minute demo
          </div>
          <div className="text-[11px] text-white/50">
            How assessments work · What proctoring checks · What your report shows
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">Live preview</div>
      </div>
    </div>
  );
}

/* ─── MCQ panel ─────────────────────────────────────────────────────── */
function McqPanel({ active }) {
  const options = [
    { key: "A", text: "Ask them to send an RFP and revisit later." },
    { key: "B", text: "Schedule a discovery call to understand their business context." },
    { key: "C", text: "Send the standard product deck and pricing." },
    { key: "D", text: "Escalate to the sales head immediately." },
  ];
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
          Q3 of 10 · Solution Selling
        </div>
        <div className="text-[10px] font-mono text-[#EE2737] font-bold">
          {active ? <Countdown from={597} /> : "09:57"}
        </div>
      </div>
      <div className="text-[13px] text-white/85 leading-snug mb-3 line-clamp-2">
        A new customer opens with &ldquo;I only have 15 minutes and I need pricing.&rdquo; What is your first move?
      </div>
      <div className="space-y-1.5">
        {options.map((o, i) => {
          const isSelected = active && i === 1;
          return (
            <div
              key={o.key}
              className={`flex items-center gap-2 px-3 py-1.5 rounded border text-[11px] transition-all ${
                isSelected
                  ? "border-[#EE2737] bg-[#EE2737]/10 text-white"
                  : "border-white/10 bg-white/[0.02] text-white/60"
              }`}
              style={{ animationDelay: `${400 + i * 120}ms` }}
            >
              <span
                className={`w-4 h-4 rounded-full border flex items-center justify-center text-[9px] font-bold ${
                  isSelected ? "border-[#EE2737] bg-[#EE2737] text-[#5F004B]" : "border-white/25 text-white/60"
                }`}
              >
                {isSelected ? <CheckCircle2 size={10} /> : o.key}
              </span>
              <span className="truncate">{o.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Sales Pitch panel ─────────────────────────────────────────────── */
function PitchPanel({ active }) {
  const line =
    "Thanks for the time. In under two minutes, I'd like to walk you through why VI is the right partner for your Bangalore-Mumbai lane…";
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
          Recording · 2 min max
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-red-400 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          REC 0:42
        </div>
      </div>

      {/* Mic waveform */}
      <div className="flex items-end justify-center gap-1 h-14 mb-3">
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className="w-1 rounded-full bg-[#EE2737]"
            style={{
              height: active ? "20%" : "12%",
              animation: active ? `pulseBar 1.2s ease-in-out ${i * 0.05}s infinite` : "none",
            }}
          />
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-1.5">
        Live transcript
      </div>
      <div className="px-3 py-2 border border-white/10 bg-white/[0.02] rounded text-[11px] text-white/80 leading-relaxed min-h-[54px]">
        {active ? <Typewriter text={line} /> : line}
      </div>

      <div className="flex items-center gap-2 mt-3 text-[10px] text-white/40">
        <Mic size={11} className="text-[#EE2737]" />
        <span>Live transcription · AI scoring against the BARS rubric</span>
      </div>

      {/* keyframes */}
      <style>{`
        @keyframes pulseBar {
          0%, 100% { transform: scaleY(0.4); opacity: 0.55; }
          50%      { transform: scaleY(1.6); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ─── Role Play panel ───────────────────────────────────────────────── */
function RolePlayPanel({ active }) {
  const bubbles = [
    { who: "ai",   text: "Why should I even talk to VI when the competitor is 30% cheaper?" },
    { who: "user", text: "Fair question. Before I answer, can I understand what business outcome you're benchmarking that price against?" },
    { who: "ai",   text: "Board wants a 10% IT cost reduction by year-end. That's the metric." },
  ];
  return (
    <div className="h-full flex flex-col justify-center">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
          Live · AI customer role play
        </div>
        <div className="text-[10px] text-[#EE2737] font-mono font-bold">Turn 2 of 5</div>
      </div>

      <div className="space-y-2 overflow-hidden">
        {bubbles.map((b, i) => (
          <div
            key={i}
            className={`flex items-start gap-2 ${b.who === "user" ? "flex-row-reverse" : ""}`}
            style={{
              opacity: active ? 0 : 1,
              animation: active ? `bubbleIn 0.55s ease-out ${0.35 + i * 0.9}s forwards` : "none",
            }}
          >
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                b.who === "ai" ? "bg-[#EE2737]/20 text-[#EE2737]" : "bg-white/10 text-white/80"
              }`}
            >
              {b.who === "ai" ? <Bot size={12} /> : <UserIcon size={12} />}
            </span>
            <div
              className={`text-[11px] leading-snug px-3 py-1.5 rounded max-w-[78%] ${
                b.who === "ai"
                  ? "bg-[#EE2737]/10 border border-[#EE2737]/25 text-white/85"
                  : "bg-white/[0.06] border border-white/10 text-white/85"
              }`}
            >
              {b.text}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes bubbleIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ─── Small helpers ─────────────────────────────────────────────────── */
function Countdown({ from }) {
  const [s, setS] = useState(from);
  useEffect(() => {
    const t = setInterval(() => setS((v) => (v > 0 ? v - 1 : from)), 1000);
    return () => clearInterval(t);
  }, [from]);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return <>{`${String(m).padStart(2, "0")}:${ss}`}</>;
}

function Typewriter({ text, speed = 28 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const t = setInterval(() => {
      setN((v) => {
        if (v >= text.length) { clearInterval(t); return v; }
        return v + 1;
      });
    }, speed);
    return () => clearInterval(t);
  }, [text, speed]);
  return (
    <>
      {text.slice(0, n)}
      <span className="inline-block w-1.5 h-3 bg-[#EE2737] ml-0.5 align-middle animate-pulse" />
    </>
  );
}
